import React, {useEffect, useMemo, useRef} from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  Linking,
  ScrollView,
  TextInput,
} from 'react-native';
import CCPackageTable from '@/Atoms/CCPackageTable';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {useState} from 'react';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {FontSizes, vs} from '@/Utils/Dimensions';
import useGetMentorCalls from '@/Hooks/MentorCallsApi/getMentorCalls';
import dayjs from 'dayjs';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import Octicons from '@expo/vector-icons/Octicons';
import CCButton from '@/Atoms/CCButton';
import SlideModal from '@/Atoms/SlideModal';
import Spacer from '@/Atoms/Spacer';
import Divider from '@/Atoms/Divider/Divider';
import useGetAvailableSlots from '@/Hooks/MentorCallsApi/getAvailableSlots';
import useRescheduleMentorCall from '@/Hooks/MentorCallsApi/rescheduleMentorCall';
import CCSelect from '@/Atoms/CCSelect';
import useBookStudentPreferredSlot from '@/Hooks/MentorCallsApi/studentPreferredSlotBook';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useBookMentorCall from '@/Hooks/MentorCallsApi/bookMentorCalls';
import CCTextInput from '@/Atoms/CCTextInput';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import useGetStudentDetails from '@/Hooks/MentorCallsApi/getStudentDetails';
import Loader from '@/Atoms/CCLoader/CCLoader';
import CCRating from '@/Atoms/CCRating';
import useSubmitCallFeedback from '@/Hooks/MentorCallsApi/submitFeedback';
import useMentorCallRequest from '@/Hooks/MentorCallsApi/mentorCallRequest';
import Tooltip from '@/Atoms/Tooltip';
import {ActiveMeetingsModal} from './ActiveMeetingsModal';
import useClassroomJoin from '@/Hooks/ClassroomInfoApi/useClassroomJoin';
import {useNavigation} from '@react-navigation/native';
import RescheduleModal from './RescheduleModel';
import {convertISTToUserTimezone} from '@/Utils/CommonUtils';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const headers = ['Date', 'Topic', 'Time', 'Session Link', 'Status', ''];
const customFlexArr = [0.6, 2, 2, 1.5, 1.5, 2];
// const headers = ['Date', 'Topic', 'Time', 'Session Link', ''];
// const customFlexArr = [0.6, 2, 2, 4, 2];
const weekdays = Array.from({length: 7}, (_, i) =>
  dayjs().day(i).format('dddd'),
);

// function to get nearest date for a weekday
function getNearestDateForDay(dayCode) {
  const targetDay = dayCode; // 0=Sunday, 1=Monday...
  const today = dayjs();
  const todayDay = today.day();

  let diff = targetDay - todayDay;
  if (diff < 0) {
    diff += 7; // move to next week if day already passed
  }
  return today.add(diff, 'day');
}

export const getStatusStyles = status => {
  if (!status) {
    return {
      textColor: '#000000',
      backgroundColor: '#FFFFFF',
      borderColor: '#CCCCCC',
    };
  }
  switch (status?.toLowerCase()) {
    case 'scheduled':
    case 'ongoing':
      return {
        textColor: CCColors.Primary.Brown,
        backgroundColor: CCColors.MeetingStatus.Magenta,
        borderColor: CCColors.MeetingStatus.Brown,
      };
    case 'updating':
      return {
        textColor: CCColors.BlueAqua,
        backgroundColor: CCColors.MeetingStatus.DarkBlue,
        borderColor: CCColors.MeetingStatus.Turquoise,
      };
    case 'completed':
      return {
        textColor: CCColors.Green,
        backgroundColor: CCColors.StatusBar.BorderAquaBlue,
        borderColor: CCColors.MeetingStatus.Emerald,
      };
    case 'cancelled':
    case 'missed':
      return {
        textColor: CCColors.Red,
        backgroundColor: CCColors.MeetingStatus.Crimson,
        borderColor: CCColors.MeetingStatus.Rust,
      };
    case 'rescheduled':
      return {
        textColor: CCColors.Purple,
        backgroundColor: CCColors.MeetingStatus.Purple,
        borderColor: CCColors.MeetingStatus.LightPurple,
      };
    default:
      return {
        textColor: CCColors.Black,
        backgroundColor: CCColors.White,
        borderColor: CCColors.MeetingStatus.Grey,
      };
  }
};

// function to get time slots for the day
function getDayTimes(startTime, endTime, intervalMinutes = 60) {
  const start = dayjs().hour(startTime).minute(0).second(0); // 9:00 AM
  const end = dayjs().hour(endTime).minute(0).second(0); // 9:00 PM

  const times = [];
  let current = start;

  while (current.isBefore(end) || current.isSame(end)) {
    times.push(current.format('hh:mm A')); // or "h:mm A" for 12h format
    current = current.add(intervalMinutes, 'minute');
  }

  return times;
}

function getFormattedTimeZone(timeZoneOnly = false) {
  const now = new Date();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timeZoneOnly) {
    return tz;
  }
  // Get offset in minutes and convert to hours/minutes
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const pad = n => String(Math.floor(Math.abs(n))).padStart(2, '0');

  const hours = pad(offsetMinutes / 60);
  const minutes = pad(offsetMinutes % 60);

  // Get human-readable name (English)
  const longName = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'long',
  })
    .formatToParts(now)
    .find(p => p.type === 'timeZoneName').value;

  return `(GMT${sign}${hours}:${minutes}) ${longName}`;
}

const scrollStep = 5;
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const MentorCalls: React.FC<any> = ({
  userId,
  isMentor,
  mobile,
  userKey,
  setPopupBlocked,
}) => {
  const navigation = useNavigation();

  const [callsData, setCallsData] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showPreferredSlotModal, setShowPreferredSlotModal] = useState(false);
  const [callId, setCallId] = useState('');
  const [mentorId, setMentorId] = useState(1);
  const [availableSlots, setAvailableSlots] = useState();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [rescheduleMentorCallResponse, setRescheduleMentorCallResponse] =
    useState<{error?: string; message?: string; result?: any} | null>(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [bookCall, setBookCall] = useState(false);
  // const [studentMobile, setStudentMobile] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [feedbackPayload, setFeedbackPayload] = useState({});
  const [bookSlotResponse, setBookSlotResponse] = useState<{
    error?: string;
    message?: string;
  } | null>(null);

  // Use shared classroom join hook
  const {
    showActiveMeetingsModal,
    activeMeetingsCount,
    activeLoading,
    endLoading,
    handleJoinClick,
    handleModalClose,
    handleModalJoin,
    handleModalEndAndJoin,
  } = useClassroomJoin();

  const {mutate: getMentorCalls} = useGetMentorCalls(
    data => {
      setCallsData(data?.result || []);
      setMentorId(data?.result?.[0]?.mentor_id || 1);
    },
    error => {
      console.log(error, 'mentor calls error');
    },
  );

  const {mutate: getMentorAvailability} = useGetAvailableSlots(
    data => {
      const slots = data?.result?.[0]?.slots ?? [];
      const grouped = slots.reduce((acc, slot) => {
        const start_time = convertISTToUserTimezone(slot.start_time);
        const end_Time = convertISTToUserTimezone(slot.end_time);

        // Extract date for grouping (in user's timezone)
        const dateKey = start_time.split('T')[0];

        console.log('Original IST:', slot.start_time);
        console.log('User TZ:', start_time);

        const timeSlot = {
          id: slot.id,
          start_time: start_time,
          end_time: end_Time,
          is_available: slot.is_available,
          blocked_reason: slot.blocked_reason,
          original_start_time: slot.start_time,
        };

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(timeSlot);
        return acc;
      }, {});
      console.log('Grouped slots', grouped);
      const firstDate = Object.keys(grouped)?.[0];
      setSelectedDate(firstDate);
      setAvailableSlots(grouped);
    },
    error => {
      console.log(error, 'mentor calls error');
    },
  );

  const {mutate: rescheduleMentorCall, isLoading: isRescheduleLoading} =
    useRescheduleMentorCall(
      data => {
        console.log('reschedule data', data);
        setRescheduleMentorCallResponse(data);
        if (showModal) {
          if (isMentor) {
            getMentorCalls({mentor_id: userId});
          } else {
            getMentorCalls({student_id: userId});
          }
          setTimeout(() => {
            setShowModal(false);
          }, 2000);
        } else {
          setCallsData(prev => {
            const updated = prev.map(call => {
              if (call.id === data.result.id) {
                // Update only the rescheduled call
                return {...call, has_completed: data.result.has_completed};
              }
              return call;
            });
            return updated;
          });
        }
      },
      error => {
        console.log('reschedule error', error);
        setRescheduleMentorCallResponse(error);
      },
    );

  const {mutate: bookPreferredSlot} = useBookStudentPreferredSlot(
    data => {
      setRescheduleMentorCallResponse(data);
    },
    error => {
      setRescheduleMentorCallResponse(error);
    },
  );

  const {mutate: bookMentorCalls, isLoading} = useBookMentorCall(
    data => {
      setBookSlotResponse(data);
      if (isMentor) {
        getMentorCalls({mentor_id: userId});
      } else {
        getMentorCalls({student_id: userId});
      }
      setShowModal(false);
      getMentorAvailability({
        mentor_id: String(mentorId),
        weeks: '2',
      });
    },
    error => {
      setBookSlotResponse(error);
    },
  );

  const {mutate: submitFeedback} = useSubmitCallFeedback(
    data => {
      setBookSlotResponse(data);
    },
    error => {
      setBookSlotResponse(error);
    },
  );

  const {mutate: mentorCallRequest} = useMentorCallRequest(
    data => {
      setBookSlotResponse(data);
    },
    error => {
      setBookSlotResponse(error);
    },
  );

  const {mutate: getStudentDetails} = useGetStudentDetails(
    data => {
      setStudentData(data.result);
      setMentorId(data.result.mentor_id);
    },
    error => {
      console.log(error, 'student data error');
    },
  );

  useEffect(() => {
    if (!isMentor) {
      getStudentDetails();
    }
  }, [isMentor, getStudentDetails]);

  // Transform mentor call item to join data format
  const transformMentorCallToJoinData = (call: any) => ({
    webinar_id: call.zoom_meeting_id,
    registrant_token: call.zoom_token,
    meeting_password: call.zoom_password,
    occurence_id: call.zoom_meeting_id,
    meeting_role: isMentor ? 'host' : 'student',
    is_zoom_meeting: 1,
    batch_type: 'mentor_call',
    webinar_topic: call.topic,
  });

  useEffect(() => {
    if (userId) {
      if (isMentor) {
        getMentorCalls({mentor_id: userId});
      } else {
        getMentorCalls({student_id: userId});
      }
    }
  }, [getMentorCalls, isMentor, userId]);

  const tableData = useMemo(
    () =>
      callsData?.map((call: any) => [
        {
          name: (
            <Box style={styles.dateCard}>
              <Box style={styles.monthbox}>
                <CCText textAlign="center" fontWeight="600">
                  {dayjs(call.scheduled_start).format('MMM')}
                </CCText>
              </Box>
              <CCText
                textAlign="center"
                fontWeight="600"
                fontSize={FontSizes[12]}>
                {dayjs(call.scheduled_start).get('date')}
              </CCText>
            </Box>
          ),
          type: 'text',
        },
        {
          name: (
            <Box>
              <CCText fontSize={FontSizes[12]}>{call.topic}</CCText>
            </Box>
          ),
          type: 'text',
        },
        {
          name: (
            <Box gap={5} type="row-center" alignItems="center">
              <Feather name="clock" size={15} />
              <CCText fontSize={FontSizes[12]}>
                {dayjs(call.scheduled_start).format('hh:mm A')}
              </CCText>
              <CCText fontSize={FontSizes[12]}>-</CCText>
              <CCText fontSize={FontSizes[12]}>
                {dayjs(call.scheduled_end).format('hh:mm A')}
              </CCText>
            </Box>
          ),
          type: 'text',
        },
        {
          name: (
            <Box
              gap={10}
              type="row-center"
              style={styles.session_link}
              // onPress={() => Linking.openURL(call.zoom_join_url)}
            >
              <Box type="row-center" gap={10} style={styles.zoom_box}>
                <Feather name="video" size={15} color="#67B3E1" />
                <CCText color="#67B3E1">Classroom</CCText>
                {/* <CCText color="#67B3E1">zoom</CCText> */}
              </Box>
              {/* <CCText width={300} fontSize={FontSizes[12]} showEllipses>
                {call.zoom_join_url}
              </CCText> */}
            </Box>
          ),
          type: 'text',
        },
        {
          name: (
            <Box
              gap={10}
              type="row-center"
              style={[
                styles.session_link,
                {
                  borderColor: getStatusStyles(call.status).borderColor,
                },
              ]}>
              <Box
                type="row-center"
                gap={10}
                style={[
                  styles.zoom_box,
                  {
                    backgroundColor: getStatusStyles(call.status)
                      .backgroundColor,
                  },
                ]}>
                <CCText color={getStatusStyles(call.status).textColor}>
                  {call.status.charAt(0).toUpperCase() + call.status.slice(1)}
                </CCText>
              </Box>
            </Box>
          ),
          type: 'text',
        },
        {
          name: (
            <Box gap={10} type="row-center-space-around">
              {call.status === 'completed' ? (
                call.feedback_received ? (
                  <Box type="row-flex-start" gap={5}>
                    <Ionicons
                      name={'checkmark-circle'}
                      size={18}
                      color={'#3DAB9E'}
                    />
                    <CCText textAlign="center" color={CCColors.Green}>
                      Feedback Received
                    </CCText>
                  </Box>
                ) : (
                  <CCButton
                    onPress={() => {
                      if (isMentor) {
                        rescheduleMentorCall({
                          call_id: call.id,
                          mark_complete: !call.has_completed,
                        });
                      } else {
                        setShowFeedbackModal(true);
                        setFeedbackPayload({
                          call_id: call.id,
                          student_id: call.student_id,
                        });
                      }
                    }}>
                    <Box type="row-center" gap={10}>
                      <Ionicons
                        size={20}
                        name={
                          isMentor
                            ? call.has_completed
                              ? 'checkmark-done-circle'
                              : 'checkmark-circle-outline'
                            : 'chatbox-ellipses-outline'
                        }
                      />
                      <CCText>
                        {isMentor
                          ? `Mark ${call.has_completed ? 'In' : ''}Complete`
                          : 'Share Feedback'}
                      </CCText>
                    </Box>
                  </CCButton>
                )
              ) : (
                /* call.status !== 'scheduled' ? (
                <Box type="row-flex-start" gap={5}>
                  <Ionicons
                    name={'alert-circle'}
                    size={18}
                    color={CCColors.Red}
                  />
                  <CCText textAlign="center" color={CCColors.Red}>
                    {call.status.toUpperCase()}
                  </CCText>
                </Box>
              ) : */ <>
                  <Tooltip
                    text={
                      dayjs(call.scheduled_start).diff(dayjs(), 'd') > 60
                        ? 'Reschedule can be done only for next 2 months.'
                        : ''
                    }
                    children={toggleEvent => (
                      <View
                        onPointerEnter={toggleEvent}
                        onPointerLeave={toggleEvent}>
                        <CCButton
                          disabled={
                            dayjs(call.scheduled_start).diff(dayjs(), 'd') > 60
                          }
                          contentContainerStyle={{
                            paddingHorizontal: 20,
                            paddingVertical: 10,
                            backgroundColor:
                              dayjs(call.scheduled_start).diff(dayjs(), 'd') >
                              60
                                ? CCColors.Grey.Shade200
                                : CCColors.White,
                            borderWidth: 1,
                            borderColor: CCColors.BorderBottom,
                            borderRadius: 32,
                          }}
                          titleStyle={{
                            color: CCColors.Primary.Dark,
                          }}
                          onPress={() => {
                            setShowModal(true);
                            setCallId(call.id);
                            setMentorId(call.mentor_id);
                            getMentorAvailability({
                              mentor_id: call.mentor_id,
                              current_date: dayjs(call.scheduled_start).format(
                                'YYYY-MM-DD',
                              ),
                              weeks: '2',
                            });
                          }}>
                          Reschedule
                        </CCButton>
                      </View>
                    )}
                  />
                  {call.status === 'scheduled' && (
                    <CCButton
                      contentContainerStyle={{
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                      }}
                      disabled={
                        call.zoom_token && call.zoom_meeting_id ? false : true
                      }
                      onPress={() => {
                        const joinData = transformMentorCallToJoinData(call);
                        const opened = handleJoinClick(joinData);
                        if (opened === false) {
                          setPopupBlocked(true);
                        }
                      }}>
                      Join
                    </CCButton>
                  )}
                </>
              )}
            </Box>
          ),
          type: 'text',
        },
      ]),
    [callsData, isMentor, handleJoinClick],
  );

  return (
    <>
      <Loader loading={activeLoading || endLoading} />
      <ScrollView>
        <Box type="row-center-between">
          <CCText style={styles.title}>Mentor Calls</CCText>
          <Box type="row-flex-start" gap={10}>
            {(isMentor || studentData?.total > studentData?.scheduled) && (
              <CCButton
                type="box"
                onPress={() => {
                  setBookCall(true);
                  setShowModal(true);
                  if (!availableSlots) {
                    getMentorAvailability({
                      mentor_id: String(mentorId),
                      weeks: '2',
                    });
                  }
                }}
                titleStyle={{color: CCColors.White}}>
                Book Mentor Session
              </CCButton>
            )}
            {/* {!isMentor && (
            <CCButton
              type="box"
              onPress={() => setShowPreferredSlotModal(true)}
              titleStyle={{color: CCColors.White}}>
              Your Preferred Slot
            </CCButton>
          )} */}
          </Box>
        </Box>
        <View style={styles.card}>
          <CCPackageTable
            headers={headers}
            tableData={tableData ?? []}
            flexArr={customFlexArr}
            borderBottomColor={CCColors.BorderBottom}
            headerTextColor={CCColors.TextColor.Grey}
            margin={0}
            jsxTable={false}
            isMargin={false} // Enables Pressable rows
            tournament={true}
            backgroundColorRow={true}
            tableBorder={false}
          />
        </View>
        <RescheduleModal
          visible={showModal}
          onClose={() => {
            setShowModal(false);
            setCallId('');
          }}
          isLoading={bookCall ? isLoading : isRescheduleLoading}
          availableSlots={availableSlots}
          isHost={isMentor}
          callId={callId}
          onBook={bookMentorCalls}
          onReschedule={rescheduleMentorCall}
          onMentorCallRequest={mentorCallRequest}
          bookCall={bookCall}
          mobile={mobile}
          userTimezone={userTimezone}
          apiResponse={
            bookCall ? bookSlotResponse : rescheduleMentorCallResponse
          }
        />
        <SlideModal
          visible={showPreferredSlotModal}
          showHeader
          showCloseIcon
          customStyle={{
            minWidth: 600,
            width: 600,
          }}
          modalHeaderTitle={
            <Box type="row-center" gap={10}>
              <Ionicons name="calendar-outline" size={15} color="#4D3F37" />
              <CCText fontSize={FontSizes[14]}>Your Preferred Slots</CCText>
            </Box>
          }
          onClose={() => setShowPreferredSlotModal(false)}>
          <Box type="row-flex-start" gap={5}>
            <CCText color={CCColors.Grey.Grey}>
              Note: To ensure a smoother experience, we would like to record
              your preferred time slot. This will help us schedule upcoming
              sessions or reschedule calls, if needed, at a time that is most
              convenient for you.
            </CCText>
          </Box>
          <Spacer spacing={20} />
          <Divider />
          <Spacer spacing={20} />
          <CCText fontSize={FontSizes[16]}>Select Date slot</CCText>
          <Spacer spacing={10} />
          {weekdays && weekdays.length > 0 ? (
            <>
              <Box flexWrap="wrap" type="row-flex-start" gap={10}>
                {weekdays.map((day, code) => (
                  <Box
                    key={day}
                    style={[
                      styles.dateCard,
                      {
                        width: 100,
                        margin: 0,
                        height: 'auto',
                        backgroundColor:
                          selectedDate ===
                          getNearestDateForDay(code).format('YYYY-MM-DD')
                            ? CCColors.Primary.Dark
                            : CCColors.White,
                      },
                    ]}
                    onPress={() => {
                      setSelectedDate(
                        getNearestDateForDay(code).format('YYYY-MM-DD'),
                      );
                    }}>
                    <CCText
                      textAlign="center"
                      fontWeight="bold"
                      fontSize={FontSizes[12]}
                      color={
                        selectedDate ===
                        getNearestDateForDay(code).format('YYYY-MM-DD')
                          ? CCColors.White
                          : CCColors.TextColor.Dark
                      }>
                      {day}
                    </CCText>
                  </Box>
                ))}
              </Box>
              <Spacer spacing={20} />
              <CCText fontSize={FontSizes[16]}>Select Time slot</CCText>
              <Spacer spacing={10} />
              <Box flexWrap="wrap" type="row-flex-start" gap={10}>
                <CCText>Available From:</CCText>
                <Box>
                  <CCSelect
                    label=""
                    headerComponent={<></>}
                    items={getDayTimes(9, 21, 30).map(ele => ({
                      label: ele,
                      value: ele,
                    }))}
                    selectedValue={startTime}
                    onValueChange={value => {
                      setStartTime(value);
                    }}
                    placeholder={'Select Time'}
                  />
                </Box>
                <CCText>to</CCText>
                <Box>
                  <CCSelect
                    headerComponent={<></>}
                    label=""
                    placeholder={'Select Time'}
                    items={getDayTimes(9, 21, 30)
                      .slice(
                        getDayTimes(9, 21, 30).indexOf(startTime) > 0
                          ? getDayTimes(9, 21, 30).indexOf(startTime)
                          : 0,
                      )
                      .map(ele => ({
                        label: ele,
                        value: ele,
                      }))}
                    selectedValue={endTime}
                    onValueChange={value => setEndTime(value)}
                  />
                </Box>
              </Box>
            </>
          ) : (
            <></>
          )}
          <Spacer spacing={20} />
          <Divider />
          <Spacer spacing={20} />
          <Box type="row-center-between">
            <CCText>{getFormattedTimeZone()}</CCText>
            <CCButton
              onPress={() =>
                bookPreferredSlot({
                  slots: [
                    {
                      start_time: dayjs(startTime, 'hh:mm A').format(
                        'HH:mm:ss',
                      ),
                      end_time: dayjs(endTime, 'hh:mm A').format('HH:mm:ss'),
                      mobile_number: mobile,
                      day_of_week: dayjs(selectedDate).day(),
                      timezone: getFormattedTimeZone(true),
                    },
                  ],
                })
              }>
              Submit
            </CCButton>
          </Box>
          {rescheduleMentorCallResponse ? (
            <>
              <Spacer spacing={10} />
              <Divider />
              <Spacer spacing={10} />
              <CCText
                fontSize={FontSizes[16]}
                color={
                  rescheduleMentorCallResponse.error
                    ? CCColors.Red
                    : CCColors.Green
                }>
                {rescheduleMentorCallResponse.error
                  ? rescheduleMentorCallResponse.error
                  : `Successfully ${
                      rescheduleMentorCallResponse.result?.[0]?.status ?? ''
                    } your preferred slot for future references`}
              </CCText>
            </>
          ) : (
            <></>
          )}
        </SlideModal>
        <SlideModal
          visible={showFeedbackModal}
          showHeader
          showCloseIcon
          customStyle={{
            minWidth: 600,
            width: 600,
          }}
          modalHeaderTitle={
            <Box type="row-center" gap={10}>
              <Ionicons
                name="chatbox-ellipses-outline"
                size={15}
                color="#4D3F37"
              />
              <CCText fontSize={FontSizes[14]}>Share Your Feeback</CCText>
            </Box>
          }
          onClose={() => setShowFeedbackModal(false)}>
          <Box type="row-flex-start" gap={30}>
            <CCText fontSize={FontSizes[16]}>Select Rating</CCText>
            <CCRating
              rating={feedbackPayload?.rating || 0}
              onChange={rating =>
                setFeedbackPayload(prev => ({...prev, rating}))
              }
            />
          </Box>
          <Spacer spacing={30} />
          <CCText fontSize={FontSizes[16]}>Provide Comment</CCText>
          <Spacer spacing={10} />
          <TextInput
            style={styles.textArea}
            multiline
            placeholder="Comments on Mentor Call"
            value={feedbackPayload?.comments || ''}
            placeholderTextColor={CCColors.Grey.Grey}
            onChangeText={text => {
              setFeedbackPayload(prev => ({...prev, comments: text}));
            }}
          />
          <Divider />
          <Spacer spacing={20} />
          <Box type="row-center-between">
            <CCText>{getFormattedTimeZone()}</CCText>
            <CCButton
              disabled={!feedbackPayload?.rating}
              onPress={() => submitFeedback(feedbackPayload)}>
              Submit
            </CCButton>
          </Box>
          <Spacer spacing={20} />
          <CCText
            fontSize={FontSizes[18]}
            color={bookSlotResponse?.error ? CCColors.Red : CCColors.Green}>
            {bookSlotResponse &&
              (bookSlotResponse?.error
                ? bookSlotResponse?.error
                : bookSlotResponse?.message)}
          </CCText>
        </SlideModal>
        <ActiveMeetingsModal
          visible={showActiveMeetingsModal}
          activeMeetingsCount={activeMeetingsCount || 0}
          onClose={handleModalClose}
          onJoin={handleModalJoin}
          onEndAndJoin={handleModalEndAndJoin}
        />
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 8,
    padding: vs(20),
    marginBottom: vs(10),
    shadowColor: '#4D3F370D',
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  dateCard: {
    backgroundColor: CCColors.White,
    borderRadius: 8,
    padding: vs(4),
    width: 50,
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#26232233',
    shadowColor: '#4D3F370D',
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
    marginVertical: vs(5),
  },
  monthbox: {
    backgroundColor: CCColors.Primary.Light,
    borderRadius: 4,
    width: '100%',
    marginBottom: vs(4),
  },
  title: {
    fontSize: FontSizes[18],
    fontWeight: '600',
    marginVertical: vs(20),
  },
  session_link: {
    borderWidth: 1,
    borderColor: '#67B3E180',
    borderRadius: 4,
    paddingVertical: vs(6),
    paddingHorizontal: vs(10),
    color: CCColors.Primary.Dark,
  },
  zoom_box: {
    backgroundColor: '#67B3E126',
    borderRadius: 4,
    paddingVertical: vs(10),
    paddingHorizontal: vs(15),
    color: CCColors.Primary.Dark,
  },
  textArea: {
    borderWidth: 1,
    borderColor: CCColors.TextColor.Grey,
    borderRadius: 8,
    padding: 10,
    minHeight: 100,
    marginBottom: 20,
    textAlignVertical: 'top',
  },
});

export default MentorCalls;
